import { useRef, useState } from 'react';
import { useClickOutside } from '../../lib/useClickOutside';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';

// Per-row "···" popover on the master grid (P2b-2 §7.1.1). Items are scoped
// to the row's current state:
//
//   - Every row:       Edit Campaign  (gated by editDisabledReason —
//                                       unassigned / opt-out rows can't
//                                       reach the row editor)
//                      Duplicate row  (disabled on opt-out rows — see §6.8)
//                      History        (always enabled — audit is read-only)
//   - Non-opt-out row: Mark as Opted Out  (gated on brand's Opt Out Campaign
//                                           per §6.7.6)
//   - Opt-out row:     Reverse Opt Out
//   - Secondary rows:  Delete row (primary rows cannot be hard-deleted)
//
// Edit Campaign shares its navigation target + disabled-reason with the
// pencil icon in the master grid's Campaign cell; both entry points are
// gated identically.
//
// Gating on the Opt Out Campaign:
//   - no campaign exists       → disabled, tooltip "Brand has no Opt Out
//                                campaign — create or activate one first"
//   - status === 'draft'       → disabled, tooltip "Brand's Opt Out campaign
//                                is in draft — activate it first"
//   - status is anything else  → disabled, tooltip "Brand's Opt Out campaign
//     that is not 'active'       is <status> — activate it first" (covers
//                                inactive / archived / any future value)

type BrandOptOutStatus =
  | { kind: 'missing' }
  | { kind: 'inactive'; status: string }
  | { kind: 'active' };

export function deriveBrandOptOutStatus(
  brandAccountId: string,
  allCampaigns: Campaign[],
): BrandOptOutStatus {
  // Pre-P2b-3 §5: brand owner is now createdInBrandAccountId. Visibility
  // (shareTargets) doesn't change which Campaign is the brand's
  // designated Opt Out — that's still a per-brand designation tracked
  // by isOptOut + the org owner.
  const found = allCampaigns.find(
    (c) => c.isOptOut && c.createdInBrandAccountId === brandAccountId,
  );
  if (!found) return { kind: 'missing' };
  if (found.status === 'active') return { kind: 'active' };
  return { kind: 'inactive', status: found.status };
}

export function optOutDisabledReason(s: BrandOptOutStatus): string | null {
  if (s.kind === 'active') return null;
  if (s.kind === 'missing') {
    return 'Brand has no Opt Out campaign — create or activate one first.';
  }
  if (s.status === 'draft') {
    return "Brand's Opt Out campaign is in draft — activate it first.";
  }
  return `Brand's Opt Out campaign is ${s.status} — activate it first.`;
}

type Props = {
  row: AccountCampaign;
  brandOptOut: BrandOptOutStatus;
  archived: boolean;
  // Edit Campaign entry (P2b-2 §7.1.1, chunk B). Parent owns the nav so the
  // pencil icon in the Campaign cell and this menu item share the same
  // target + gating. editDisabledReason === null means the item is
  // clickable; a non-null string is used as the tooltip on the disabled
  // item.
  onEditCampaign: () => void;
  editDisabledReason: string | null;
  // Duplicate + History entries (chunk C). Parent owns both flows — the
  // menu only emits the intent.
  onDuplicate: () => void;
  onHistory: () => void;
  onMarkOptOut: () => void;
  onReverseOptOut: () => void;
  onDeleteSecondary: () => void;
  // Ad Preview Mockups Stage 1 §3.1. Generate / re-generate preview URL
  // for this row. Hidden on unassigned + opt-out rows because the server
  // gates generation on rowType === 'campaign' + non-null campaignId.
  // Parent owns the modal; the menu only emits the intent.
  onGeneratePreview: () => void;
  // OrganicAssets spec §3.2 / §6.3. Create this row's Local + Organic Drive
  // folders. Shown only on campaign-type rows; the parent computes the
  // disabled reason (brand folder not configured / both folders already
  // exist / archived) so the AM doesn't see an action that would 400.
  onCreateFolders: () => void;
  createFoldersDisabledReason: string | null;
};

export function RowActionsMenu({
  row,
  brandOptOut,
  archived,
  onEditCampaign,
  editDisabledReason,
  onDuplicate,
  onHistory,
  onMarkOptOut,
  onReverseOptOut,
  onDeleteSecondary,
  onGeneratePreview,
  onCreateFolders,
  createFoldersDisabledReason,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside([rootRef], () => setOpen(false), open);

  const isOptOut = row.rowType === 'opt_out';
  const isUnassigned = row.rowType === 'unassigned';
  const optOutBlockedReason = optOutDisabledReason(brandOptOut);
  // Ad Preview Mockups Stage 1 §3.1. Mirrors the server-side gate in
  // loadRowForGeneration so the AM doesn't see an enticing button that
  // would 400 on click.
  const canGeneratePreview = !isUnassigned && !isOptOut;
  // OrganicAssets §3.2: only campaign-type rows can create asset folders.
  const canCreateFolders = !isUnassigned && !isOptOut;
  const hasExistingPreview = !!row.latestPreviewLinkId;
  const previewLabel = hasExistingPreview ? 'Re-generate Preview' : 'Generate Preview';

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        disabled={archived}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded px-1.5 py-0.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
        title="Row actions"
      >
        ···
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-slate-200 bg-white shadow-lg py-1 text-sm"
        >
          <button
            type="button"
            role="menuitem"
            disabled={editDisabledReason !== null}
            onClick={run(onEditCampaign)}
            title={editDisabledReason ?? undefined}
            className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Edit Campaign
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={isOptOut}
            onClick={run(onDuplicate)}
            title={isOptOut ? 'Opt-out rows cannot be duplicated.' : undefined}
            className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Duplicate row
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={run(onHistory)}
            className="block w-full px-3 py-1.5 text-left hover:bg-slate-50"
          >
            History
          </button>
          {canGeneratePreview && (
            <button
              type="button"
              role="menuitem"
              onClick={run(onGeneratePreview)}
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50"
            >
              {previewLabel}
            </button>
          )}
          {canCreateFolders && (
            <button
              type="button"
              role="menuitem"
              disabled={createFoldersDisabledReason !== null}
              onClick={run(onCreateFolders)}
              title={createFoldersDisabledReason ?? undefined}
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Create Meta Asset Folder
            </button>
          )}
          {!isOptOut && (
            <button
              type="button"
              role="menuitem"
              disabled={optOutBlockedReason !== null}
              onClick={run(onMarkOptOut)}
              title={optOutBlockedReason ?? undefined}
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Mark as Opted Out
            </button>
          )}
          {isOptOut && (
            <button
              type="button"
              role="menuitem"
              onClick={run(onReverseOptOut)}
              className="block w-full px-3 py-1.5 text-left hover:bg-slate-50"
            >
              Reverse Opt Out
            </button>
          )}
          {!row.primary && (
            <button
              type="button"
              role="menuitem"
              onClick={run(onDeleteSecondary)}
              className="block w-full px-3 py-1.5 text-left text-red-600 hover:bg-red-50"
            >
              Delete row
            </button>
          )}
        </div>
      )}
    </div>
  );
}
